/**
 * activeCharacter.ts
 * Module-level singleton that stores the currently selected playable character.
 *
 * Set once at game-screen mount (from SaveData) and read by the renderer
 * each frame to pick the correct sprite sheet, clips, and draw offset.
 * Not part of GameState because character selection doesn't change mid-level.
 *
 * @module activeCharacter
 */

import type { CharacterId, CharacterMeta } from '../types/character';
import { CHARACTERS, getCharacter } from './characterData';

/** Module-level selected character — defaults to Nova. */
let _activeId: CharacterId = 'nova';

/**
 * Set the active playable character by id.
 * Call this once when the game screen mounts, before the first frame.
 *
 * @param id - The character to activate.
 */
export function setActiveCharacter(id: CharacterId): void {
  _activeId = id;
}

/**
 * Get the id of the currently active character.
 *
 * @returns The active CharacterId.
 */
export function getActiveCharacterId(): CharacterId {
  return _activeId;
}

/**
 * Get the full metadata for the currently active character.
 *
 * @returns The active CharacterMeta (sheet, clips, drawOffset, etc.).
 */
export function getActiveCharacter(): CharacterMeta {
  return getCharacter(_activeId);
}

/**
 * Look up the character registry directly by id.
 * Convenience re-export so callers don't need to import characterData separately.
 *
 * @param id - Character identifier.
 * @returns The CharacterMeta for the given id.
 */
export { CHARACTERS, getCharacter };
